import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import {
	FlatRowInput,
	type FlatRowInputProps,
} from "web_myplann/Shared/Components/FlatRow/FlatRowInput";
import { formatThousands, parseThousands } from "web_myplann/Utils/formatters";

type Props<FormType extends FieldValues, TName> = Omit<
	FlatRowInputProps,
	"value" | "onChange" | "onBlur" | "inputRef" | "error" | "helperText"
> & {
	control: Control<FormType>;
	name: TName;
};

export const FlatRowFormInput = <
	FormType extends FieldValues,
	TName extends FieldPath<FormType>,
>({
	control,
	name,
	type = "text",
	inputMode,
	...rest
}: Props<FormType, TName>) => {
	const isNumber = type === "number";
	// type="number" disables our custom thousands rendering — render as text instead
	const effectiveType = isNumber ? "text" : type;
	const effectiveInputMode = isNumber ? "numeric" : inputMode;

	return (
		<Controller
			control={control}
			name={name}
			render={({
				field: { value, onChange, onBlur, ref },
				fieldState: { error },
			}) => {
				const stringValue = value == null ? "" : String(value);
				const displayValue = isNumber
					? formatThousands(stringValue)
					: stringValue;

				const handleChange = (next: string) => {
					if (isNumber) {
						onChange(parseThousands(next));
					} else {
						onChange(next);
					}
				};

				return (
					<FlatRowInput
						{...rest}
						type={effectiveType}
						inputMode={effectiveInputMode}
						value={displayValue}
						onChange={handleChange}
						onBlur={onBlur}
						inputRef={ref}
						error={!!error?.message}
						helperText={error?.message}
					/>
				);
			}}
		/>
	);
};
